import { type ReactNode } from 'react';

export type PaginationProps = {
  /** 1-indexed current page. */
  currentPage: number;
  /** Total pages — must be >= 1. */
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Override the nav's aria-label. */
  ariaLabel?: string;
  /**
   * Sibling count around `currentPage`. Default 1.
   * sibling=1 ⇒ shows current-1, current, current+1.
   * sibling=2 ⇒ shows current-2..current+2.
   */
  siblingCount?: number;
  /** Optional className for the outer `<nav>`. */
  className?: string;
};

/**
 * Pagination — keyboard-friendly numeric pager with ellipsis collapse.
 *
 * Renders a `<nav aria-label>` with buttons: Prev, page numbers (with `…`
 * collapsing), Next. The current page button has `aria-current="page"`.
 *
 * NO routing logic — the parent owns query string or local state. This
 * keeps the component reusable across `/my-tickets` (server list) and
 * client-side filter lists.
 *
 * @example
 *   const [page, setPage] = useState(1);
 *   <Pagination currentPage={page} totalPages={42} onPageChange={setPage} />
 *
 * @example
 *   // With router integration (controlled outside):
 *   <Pagination
 *     currentPage={Number(searchParams.get('page') ?? 1)}
 *     totalPages={Math.ceil(total / pageSize)}
 *     onPageChange={(p) => setSearchParams({ page: String(p) })}
 *   />
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  ariaLabel = 'Paginación',
  siblingCount = 1,
  className = '',
}: PaginationProps): JSX.Element | null {
  if (totalPages < 1) return null;
  const clampedTotal = Math.max(1, Math.floor(totalPages));
  const clampedCurrent = Math.min(clampedTotal, Math.max(1, Math.floor(currentPage)));
  const items = buildPageList(clampedCurrent, clampedTotal, Math.max(0, siblingCount));

  function go(page: number): void {
    if (page < 1 || page > clampedTotal || page === clampedCurrent) return;
    onPageChange(page);
  }

  return (
    <nav aria-label={ariaLabel} className={`flex items-center gap-1 ${className}`.trim()}>
      <PageButton
        ariaLabel="Página anterior"
        disabled={clampedCurrent === 1}
        onClick={() => go(clampedCurrent - 1)}
      >
        <span aria-hidden>←</span>
      </PageButton>
      {items.map((item, idx) =>
        item === 'gap' ? (
          <span key={`gap-${idx}`} className="px-2 text-fc-muted" aria-hidden>
            …
          </span>
        ) : (
          <PageButton
            key={item}
            ariaLabel={`Página ${item}`}
            ariaCurrent={item === clampedCurrent ? 'page' : undefined}
            active={item === clampedCurrent}
            onClick={() => go(item)}
          >
            {item}
          </PageButton>
        ),
      )}
      <PageButton
        ariaLabel="Página siguiente"
        disabled={clampedCurrent === clampedTotal}
        onClick={() => go(clampedCurrent + 1)}
      >
        <span aria-hidden>→</span>
      </PageButton>
    </nav>
  );
}

// ---- internals ------------------------------------------------------------

type PageItem = number | 'gap';

/**
 * Build the page list with ellipsis collapse. Always includes page 1 and
 * the last page; collapses interior ranges to a single 'gap' marker.
 *
 * Strategy: show first, last, current ± siblingCount. If the gap between
 * these blocks is just 1 page, render it as a number (avoids `1 … 2 3 4`).
 */
// eslint-disable-next-line react-refresh/only-export-components -- utilidad pura colocada junto a su consumidor; no afecta fast-refresh en runtime.
export function buildPageList(current: number, total: number, sibling: number): PageItem[] {
  if (total <= 1) return [1];

  const items: PageItem[] = [];
  const minVisible = 1;
  const maxVisible = total;

  const leftBound = Math.max(minVisible + 1, current - sibling);
  const rightBound = Math.min(maxVisible - 1, current + sibling);

  items.push(1);

  if (leftBound > minVisible + 1) {
    items.push('gap');
  }
  for (let i = leftBound; i <= rightBound; i++) {
    items.push(i);
  }
  if (rightBound < maxVisible - 1) {
    items.push('gap');
  }
  if (maxVisible > minVisible) {
    items.push(maxVisible);
  }

  return items;
}

type PageButtonProps = {
  children: ReactNode;
  onClick: () => void;
  ariaLabel: string;
  ariaCurrent?: 'page';
  active?: boolean;
  disabled?: boolean;
};

function PageButton({
  children,
  onClick,
  ariaLabel,
  ariaCurrent,
  active = false,
  disabled = false,
}: PageButtonProps): JSX.Element {
  const base =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-card px-2 text-sm transition-colors ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fc-gold ' +
    'disabled:cursor-not-allowed disabled:opacity-50';
  const variant = active
    ? 'bg-fc-ink text-fc-paper'
    : 'border border-fc-line bg-fc-paper text-fc-ink hover:bg-fc-line/40';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      className={`${base} ${variant}`}
    >
      {children}
    </button>
  );
}
